import React, { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import TreeView from "@material-ui/lab/TreeView";
import TreeItem from "@material-ui/lab/TreeItem";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { CircularProgress } from "@mui/material";
import {
  calculateDateRanges,
  formatLocationValue,
  findExpandedNodes,
  buildLocationParentPath,
  updateCollapsedNodes,
  updateExpandedNodes,
  findNodesToLoad,
  calculateNodeChanges,
} from "../../utils/PerformanceReportUtils";
import { getNextDimensionFromArray } from "../../utils/KpiConfigUtils";
import { LocationTreeNode, LocationTreeViewProps } from "./PerformanceTypes";
import {
  LocationTreeContainer,
  LocationTreeHeader,
  LocationTreeTitle,
  LocationTreeContent,
  StyledTreeView,
  LocationTreeNodeLabel,
  LocationNodeName,
  LocationNodeDashLine,
  LocationNodeValue,
  NodeLoadingSpinner,
  FallbackContainer,
  FallbackIcon,
  SkeletonContainer,
  SkeletonBar,
  LoadingContainer,
  LoadingText,
} from "components/performance/LocationTreeStyles";
import { fetchLocationTreeData } from "../../store/performance-report/PerformanceReportActions";
import {
  selectLocationTreeData,
  selectLocationTreeIsLoading,
  selectLocationTreeHasData,
  selectPerformanceReportStoreId,
  selectPerformanceReportDateRange,
  selectIsReportAPIAvailable,
} from "../../store/performance-report/PerformanceReportSelectors";

const LocationTreeView: React.FC<LocationTreeViewProps> = ({
  metricId,
  className,
  title,
  dimensionIds = ["country", "region", "city"],
  sortBy,
  limit = 10,
}) => {
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [userCollapsedNodes, setUserCollapsedNodes] = useState<Set<string>>(
    new Set(),
  );
  const dispatch = useDispatch();
  const intl = useIntl();
  const storeId = useSelector(selectPerformanceReportStoreId);
  const performanceDateRange = useSelector(selectPerformanceReportDateRange);
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);
  const treeData = useSelector(selectLocationTreeData);
  const isLoading = useSelector(selectLocationTreeIsLoading);
  const hasData = useSelector(selectLocationTreeHasData);
  const rootDimension = dimensionIds[0] || "country";
  const lastDimension = dimensionIds[dimensionIds.length - 1] || "city";

  const dateRange = React.useMemo(() => {
    if (performanceDateRange) {
      const { currentPeriod } = calculateDateRanges(performanceDateRange);
      return currentPeriod;
    }
    return null;
  }, [performanceDateRange]);

  useEffect(() => {
    if (isReportAPIAvailable && storeId && dateRange) {
      dispatch(
        fetchLocationTreeData({
          storeId,
          dateRange,
          dimension: rootDimension,
          metricId,
          limit,
          sortBy,
        }),
      );
    }
  }, [
    dispatch,
    storeId,
    dateRange,
    metricId,
    isReportAPIAvailable,
    rootDimension,
    limit,
    sortBy,
  ]);

  useEffect(() => {
    const expandedFromData = findExpandedNodes(treeData, userCollapsedNodes);
    if (expandedFromData.length > 0) {
      setExpandedNodes((prev) => {
        const filteredExpanded = expandedFromData.filter(
          (id) => !userCollapsedNodes.has(id),
        );
        const newExpanded = [...new Set([...prev, ...filteredExpanded])];
        return newExpanded;
      });
    }
  }, [treeData, userCollapsedNodes]);

  const handleNodeExpand = useCallback(
    (nodeId: string, node: LocationTreeNode) => {
      if (!node.children || node.children.length === 0) {
        const newParentPath = buildLocationParentPath(node);
        const nextDimension = getNextDimensionFromArray(
          node.dimension,
          dimensionIds,
        );
        if (nextDimension && storeId && dateRange) {
          dispatch(
            fetchLocationTreeData({
              storeId,
              dateRange,
              dimension: nextDimension,
              dimensionBreakdown: newParentPath,
              parentNodeId: nodeId,
              metricId,
              sortBy,
            }),
          );
        }
      }
    },
    [dispatch, storeId, dateRange, metricId, dimensionIds, sortBy],
  );

  const handleNodeToggle = useCallback(
    (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
      const { newlyCollapsed, newlyExpanded } = calculateNodeChanges(
        expandedNodes,
        nodeIds,
      );
      if (newlyCollapsed.length > 0) {
        setUserCollapsedNodes((prev) =>
          updateCollapsedNodes(prev, newlyCollapsed),
        );
      }
      if (newlyExpanded.length > 0) {
        setUserCollapsedNodes((prev) =>
          updateExpandedNodes(prev, newlyExpanded),
        );
      }
      setExpandedNodes(nodeIds);
      const nodesToLoad = findNodesToLoad(treeData, newlyExpanded);
      nodesToLoad.forEach(({ nodeId, node }) => {
        handleNodeExpand(nodeId, node);
      });
    },
    [expandedNodes, treeData, handleNodeExpand],
  );

  const treeTitle =
    title ||
    intl.formatMessage({
      id: "performance.location.ordersByLocation",
      defaultMessage: "Orders by Location",
    });

  const renderTreeNode = useCallback(
    (node: LocationTreeNode) => {
      const nodeLabel = (
        <LocationTreeNodeLabel>
          <LocationNodeName title={node.dimensionValue}>
            {node.dimensionValue}
          </LocationNodeName>
          <LocationNodeDashLine />
          <LocationNodeValue>
            {formatLocationValue(node.metricValue || 0)}
          </LocationNodeValue>
          {node.isLoading && <NodeLoadingSpinner />}
        </LocationTreeNodeLabel>
      );
      return (
        <TreeItem key={node.id} nodeId={node.id} label={nodeLabel}>
          {Array.isArray(node.children) && node.children.length > 0
            ? node.children.map((childNode) => {
                return renderTreeNode(childNode);
              })
            : node.hasChildren && node.dimension !== lastDimension
              ? Array.from(Array(3)).map((_, index) => (
                  <SkeletonContainer key={`${node.id}-${index}`}>
                    <SkeletonBar />
                  </SkeletonContainer>
                ))
              : null}
        </TreeItem>
      );
    },
    [lastDimension],
  );

  if (!isReportAPIAvailable && !hasData) {
    return (
      <LocationTreeContainer className={className}>
        <LocationTreeHeader>
          <LocationTreeTitle>{treeTitle}</LocationTreeTitle>
        </LocationTreeHeader>
        <FallbackContainer>
          <FallbackIcon className="fas fa-minus" />
        </FallbackContainer>
      </LocationTreeContainer>
    );
  }

  if (isLoading && !hasData) {
    return (
      <LocationTreeContainer className={className}>
        <LocationTreeHeader>
          <LocationTreeTitle>{treeTitle}</LocationTreeTitle>
        </LocationTreeHeader>
        <LoadingContainer>
          <CircularProgress size={40} />
          <LoadingText>
            {intl.formatMessage({
              id: "performance.location.loadingLocations",
              defaultMessage: "Loading Locations...",
            })}
          </LoadingText>
        </LoadingContainer>
      </LocationTreeContainer>
    );
  }

  return (
    <LocationTreeContainer className={className}>
      <LocationTreeHeader>
        <LocationTreeTitle>{treeTitle}</LocationTreeTitle>
      </LocationTreeHeader>
      <LocationTreeContent>
        <StyledTreeView>
          <TreeView
            defaultCollapseIcon={<RemoveIcon />}
            defaultExpandIcon={<AddIcon />}
            expanded={expandedNodes}
            selected={[]}
            onNodeToggle={handleNodeToggle}
            disableSelection={true}
          >
            {treeData && treeData.length > 0 ? (
              treeData.map(renderTreeNode)
            ) : (
              <SkeletonContainer>
                <SkeletonBar />
              </SkeletonContainer>
            )}
          </TreeView>
        </StyledTreeView>
      </LocationTreeContent>
    </LocationTreeContainer>
  );
};

export default LocationTreeView;
